import { FormikHelpers } from 'formik';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ERROR_TYPES, OLD_CAR_ERROR_MSG, UNABLE_TO_ASSIST_ERROR_MSG } from '../constants/tempInfos';
import {
  TemporaryData,
  useDecodeAffiliateCarDetailsLazyQuery,
  useKbbVinLazyQuery,
  useTemporaryInfoUpdateMutation,
} from '../gql/generated/graphql';
import { devIgnoreExistingVinOverride } from '../pages/flowEntryPages/components/AffiliateDevToolbar';
import hotjar from '../services/analytics/hotjar';
import { RudderEvent, rudderanalytics } from '../services/analytics/rudderstack';
import validateLicenseNumber, {
  INVALID_LICENSE_PLATE_MSG,
  INVALID_VIN_MSG,
} from '../utils/validation/licenseNumberValidator';
import validateVin from '../utils/validation/vinValidator';
import { CookieKeys, useCookie } from './useCookie';

const DECODING_SERVICE_ISSUE_MSG_SHORT =
  'Our decoding service is experiencing issues. Please try again later.';

type KbbOption = { label: string; value: string };

type BaseCarInfo = {
  license_plate_number: string;
  license_plate_state: string;
  vin: string;
  make: string;
  model: string;
  kbb_trim_name: string;
  fuel_type: string;
  vehicle_type: string;
};

type DecodeCarParams<T extends BaseCarInfo> = {
  values: T;
  setFieldError: FormikHelpers<T>['setFieldError'];
  setValues: FormikHelpers<T>['setValues'];
};

type UseDecodeCarResult = {
  fetchedCarDetails: TemporaryData | undefined;
  setFetchedCarDetails: (details: TemporaryData | undefined) => void;
  kbbOptions: KbbOption[];
  setKbbOptions: (options: KbbOption[]) => void;
  decodingLoading: boolean;
  dealExists: boolean;
  flowStartError: string;
  vinData: ReturnType<typeof useKbbVinLazyQuery>[1]['data'];
  handleDecodeCar: <T extends BaseCarInfo>(
    params: DecodeCarParams<T>,
    isLicensePlate: boolean,
    onUpdateAccordionState?: () => void,
  ) => Promise<void>;
};

function useDecodeCar(): UseDecodeCarResult {
  const [fetchedCarDetails, setFetchedCarDetails] = useState<TemporaryData | undefined>(undefined);
  const [kbbOptions, setKbbOptions] = useState<KbbOption[]>([]);
  const [decodingLoading, setDecodingLoading] = useState(false);
  const [dealExists, setDealExists] = useState(false);
  const [flowStartError, setFlowStartError] = useState('');

  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);
  const ignoreExistingVin = useAtomValue(devIgnoreExistingVinOverride);
  const [decodeCar] = useDecodeAffiliateCarDetailsLazyQuery({ context: { isErrorHandled: true } });
  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation({
    context: { isErrorHandled: true },
  });
  const [getKbbDetails, { data: vinData }] = useKbbVinLazyQuery({
    context: { isErrorHandled: true },
  });
  const { pathname } = useLocation();

  const handleDecodeCar = async <T extends BaseCarInfo>(
    { values, setFieldError, setValues }: DecodeCarParams<T>,
    isLicensePlate: boolean,
    onUpdateAccordionState?: () => void,
  ): Promise<void> => {
    setDealExists(false);
    setFlowStartError('');

    const vin = values.vin?.toUpperCase();
    const licensePlate = values.license_plate_number?.replace(/\s/g, '')?.toUpperCase();
    const state = values.license_plate_state?.toUpperCase();

    const isVin = !isLicensePlate;

    if ((isVin && !vin) || (!isVin && !licensePlate)) {
      setFlowStartError('Please enter a value');
      return;
    }

    if (isVin && !validateVin(vin)) {
      setFieldError('vin', INVALID_VIN_MSG);
      return;
    }
    if (!isVin && !validateLicenseNumber({ licenseNumber: licensePlate })) {
      setFieldError('license_plate_number', INVALID_LICENSE_PLATE_MSG);
      return;
    }

    onUpdateAccordionState?.();

    setFetchedCarDetails(undefined);
    setDecodingLoading(true);

    try {
      const { data: decodedCar, error } = await decodeCar({
        variables: {
          vin,
          license_plate_number: licensePlate,
          license_plate_state: state,
          ignore_existing_deal: ignoreExistingVin,
          temp_info_id: guid,
        },
        context: {
          isErrorHandled: true,
        },
      });

      if (error) {
        rudderanalytics.track(RudderEvent.Error, {
          error_message: error.message,
          vin,
          license_plate: licensePlate,
          license_plate_state: state,
          pathname,
          date: new Date().toISOString(),
        });

        if (error.message === ERROR_TYPES.DEAL_EXISTS) {
          setDealExists(true);
        }
        if (error.message === ERROR_TYPES.OLD_CAR) {
          setFlowStartError(OLD_CAR_ERROR_MSG);
        }
        if (error.message === ERROR_TYPES.RESTRICTED_CAR) {
          setFlowStartError(UNABLE_TO_ASSIST_ERROR_MSG);
        }
        if (error.message === ERROR_TYPES.DECODING_SERVICE_ISSUE) {
          setFieldError('vin', DECODING_SERVICE_ISSUE_MSG_SHORT);
        }
        if (error.message === ERROR_TYPES.INVALID_LICENSE_PLATE) {
          setFieldError('license_plate_number', INVALID_LICENSE_PLATE_MSG);
        }

        return;
      }

      const carDetails = decodedCar?.decodeAffiliateCarDetails ?? undefined;

      if (carDetails?.vin) {
        hotjar.identify(carDetails.vin);
      }

      setFetchedCarDetails(carDetails);

      const { data: kbbVinData } = await getKbbDetails({
        variables: { vin: carDetails?.vin as string },
      });

      const uniqueTrimNames = Array.from(
        new Set(kbbVinData?.kbbVin?.vinResults?.map((vinResult) => vinResult?.trimName || '')),
      );
      const options = uniqueTrimNames.map((trimName) => ({ label: trimName, value: trimName }));

      setKbbOptions(options);

      setValues({
        ...values,
        vin: carDetails?.vin ?? '',
        fuel_type: carDetails?.fuel_type ?? '',
        vehicle_type: carDetails?.vehicle_type ?? '',
        kbb_trim_name: options.length === 1 ? options[0].value : '',
        make: carDetails?.make ?? '',
        model: carDetails?.model ?? '',
      } as T);

      await updateTemporaryInfo({
        variables: {
          info: {
            id: guid,
            data: {
              license_plate_number: carDetails?.license_plate_number,
              license_plate_state: carDetails?.license_plate_state,
              vin: carDetails?.vin,
              year: carDetails?.year,
              make: carDetails?.make,
              model: carDetails?.model,
              fuel_type: carDetails?.fuel_type,
              vehicle_type: carDetails?.vehicle_type,
              kbb_trim_name: options.length === 1 ? options[0].value : '',
            },
          },
        },
      });
    } finally {
      setDecodingLoading(false);
    }
  };

  return {
    fetchedCarDetails,
    setFetchedCarDetails,
    kbbOptions,
    setKbbOptions,
    decodingLoading,
    dealExists,
    flowStartError,
    vinData,
    handleDecodeCar,
  };
}

export default useDecodeCar;
